import clsx from 'clsx'
import React, { useState, useEffect } from 'react'

import { Link, Heading, Paragraph, CustomImage, GridContainer, SectionContainer } from '../index'
import Styles from './HeroHome.module.scss'
import useIntroEnded from '../../hooks/useIntroEnded'

const HeroHome = ({
  ctaA,
  ctaB,
  imageA,
  imageB,
  headingA,
  headingB,
  descriptionA,
  descriptionB,
}) => {
  const [startAnimation, setStartAnimation] = useState(false)
  const [initAnimation, setInitAnimation] = useState(true)
  const { introEnded } = useIntroEnded()

  const cardsData = [
    {
      cta: ctaA,
      image: imageA,
      heading: headingA,
      description: descriptionA,
    },
    {
      cta: ctaB,
      image: imageB,
      heading: headingB,
      description: descriptionB,
    },
  ]

  useEffect(() => {
    if (introEnded) {
      setStartAnimation(true)
      setTimeout(() => {
        setInitAnimation(false)
      }, 300)
    }

    return () => {
      setStartAnimation(false)
    }
  }, [introEnded])

  return (
    <SectionContainer className='pb-30 pt-70 md:pb-120 md:pt-160'>
      <h1 className='sr-only' tabIndex='-1'>
        Welcome to Big-D Dovetail / Resorts &amp; Residences
      </h1>
      <div className={'p-16 md:p-32'}>
        <GridContainer className={clsx(Styles.grid, 'overflow-hidden')}>
          <div className={'col-span-9 aspect-w-1 aspect-h-2 md:aspect-w-12 md:aspect-h-5'}>
            <div className={clsx('flex flex-col w-full h-full md:flex-row')}>
              {cardsData &&
                cardsData?.map((card, i) => (
                  <div
                    className={clsx(
                      Styles.cardContainer,
                      'relative',
                      `card-${i}`,
                      'md:first:pb-0 md:first:pt-0',
                      'md:last:mt-0 last:mt-24',
                      'md:first:pr-8 md:last:pl-8',
                      'overflow-hidden'
                    )}
                    key={card.heading}>
                    <Link href={card.cta?.url[0]?.uri} prefetch={false}>
                      <a
                        className={clsx(
                          Styles.card,
                          i === 0 && Styles.card_left,
                          i === 1 && Styles.card_right,
                          'flex flex-col items-center justify-start h-full'
                        )}>
                        {/* Content - Image */}
                        <div className={'relative w-full h-full z-0'}>
                          <CustomImage
                            priority={true}
                            dataPreload={true}
                            src={card.image?.sourceUrl}
                            alt={card.image?.altText}
                            imageClassName={clsx(
                              'h-full transform transition duration-1000 ease-out-bigd',
                              'bg-image',
                              startAnimation && 'scale-110',
                              !startAnimation && 'scale-125'
                            )}
                            aspectRatio='md:h-full md:w-auto'
                          />
                        </div>

                        {/* Content - Copy */}
                        <div
                          className={clsx(
                            'absolute bottom-0 left-0 right-0 top-0 flex flex-col justify-end p-20 overflow-hidden md:p-24',
                            i === 0 && 'items-start',
                            i === 1 && 'md:items-end',
                            'z-10'
                          )}>
                          <div className={clsx(Styles.description, 'mb-20 md:mb-24')}>
                            <Paragraph
                              text={card.description}
                              overrideClassName={clsx(
                                'text-white text-sm-A',
                                i === 1 && 'md:text-right',
                                'transition duration-500 delay-500 ease-out-bigd',
                                startAnimation && 'opacity-100',
                                !startAnimation && 'opacity-0'
                              )}
                            />
                          </div>
                          <Heading
                            semantics='h2'
                            text={card?.heading}
                            className={clsx(
                              Styles.heading,
                              '-ml-4 text-white uppercase md:-ml-6 md:mr-6',
                              'transition duration-1000 delay-1000 ease-out-bigd',
                              // special case on home hero
                              'h2-scale-down-B', // same font size between card titles
                              'text-4xl-D md:text-6xl-C', // desktop
                              'z-10',
                              initAnimation && Styles.headingInit
                            )}
                          />
                        </div>
                        <div
                          className={clsx(
                            'absolute bottom-0 left-0 right-0 top-0 bg-secondary transition duration-500 ease-out-bigd',
                            startAnimation && 'opacity-0',
                            !startAnimation && 'opacity-100'
                          )}></div>
                      </a>
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        </GridContainer>
      </div>
    </SectionContainer>
  )
}

export default React.memo(HeroHome)
